import { useCallback } from "react";
import { keyboardLayout } from "../lib/keyboard";

/**
 * While an on-screen keyboard is open, marks the workspace `data-keyboard` and gives it the visible
 * area as CSS variables, so its sheets sit above the keyboard (and iOS 26's floating toolbar)
 * instead of behind them. Returns the ref for the workspace root. Written straight to the element:
 * it changes on every keyboard frame.
 */
export function useKeyboardLayout(): (element: HTMLElement | null) => (() => void) | undefined {
  return useCallback((element: HTMLElement | null) => {
    const viewport = window.visualViewport;
    if (!viewport || !element) return undefined;
    const update = () => {
      const layout = keyboardLayout(window.innerHeight, viewport, navigator.userAgent);
      if (!layout.open) {
        delete element.dataset.keyboard;
        return;
      }
      element.dataset.keyboard = "";
      element.style.setProperty("--bw-view-top", `${layout.top}px`);
      element.style.setProperty("--bw-view-h", `${layout.height}px`);
      element.style.setProperty("--bw-keyboard", `${layout.bottom}px`);
      element.style.setProperty("--bw-toolbar", `${layout.toolbar}px`);
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, []);
}
